import { FC } from 'react';
import { getBezierPath, EdgeLabelRenderer, BaseEdge } from 'reactflow';
import type { EdgeProps } from 'reactflow';
import { AlertTriangle } from 'lucide-react';

export interface ErrorEdgeData {
  error_type?: string;
  error_message?: string;
  recovery_action?: string;
}

const ErrorEdge: FC<EdgeProps<ErrorEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data = {},
  selected,
  markerEnd,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: '#EF4444', // accent-red
          strokeWidth: selected ? 4 : 3,
          strokeDasharray: '8,4',
          opacity: selected ? 1 : 1,
        }}
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan group"
        >
          {/* Label Badge */}
          <div
            className="
              flex items-center gap-1.5 
              px-2.5 py-1.5 
              bg-accent-red
              text-white 
              text-xs font-semibold 
              rounded-full shadow-md
              border-2 border-white dark:border-card
              transition-all duration-fast
              hover:scale-110 hover:shadow-lg
              animate-glow-pulse
            "
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{data.error_type || 'Error'}</span>
          </div>

          {/* Hover Tooltip */}
          {(data.error_message || data.recovery_action) && (
            <div
              className="
                absolute top-10 left-1/2 -translate-x-1/2
                bg-popover
                text-xs px-3 py-2.5 
                rounded-lg shadow-lg
                border-2 border-accent-red
                opacity-0 group-hover:opacity-100
                transition-opacity duration-fast
                pointer-events-none
                z-50
                min-w-max max-w-xs
                space-y-2
              "
            >
              {data.error_message && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <div className="text-accent-red font-medium">Error:</div>
                    <div className="text-foreground">
                      {data.error_message}
                    </div>
                  </div>
                </div>
              )}
              {data.recovery_action && (
                <div className="pt-2 border-t border-border-subtle">
                  <div className="text-accent-yellow font-medium mb-1">Recovery:</div>
                  <div className="text-muted-foreground">
                    {data.recovery_action}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default ErrorEdge;

